import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import {
  readStoredTableStateWithPolicy,
  writeStoredTableStateWithPolicy,
  type TableStatePolicy,
} from '../lib/tableState';

type TableStorages = {
  session: Storage;
  local?: Storage;
};

const EMPTY_KEYS: readonly never[] = [];

function getTableStorages(): TableStorages | null {
  if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return null;
  try {
    const session = window.sessionStorage;
    const local =
      typeof window.localStorage === 'undefined' ? undefined : window.localStorage;
    return { session, local };
  } catch {
    return null;
  }
}

export function usePersistedTableState<T extends object>(
  userId: string | undefined,
  tableId: string,
  defaults: T,
  policy: TableStatePolicy<T> = {},
): readonly [T, Dispatch<SetStateAction<T>>] {
  const persistSorting = Boolean(policy.persistSorting);
  const persistFilters = Boolean(policy.persistFilters);
  const sortingKeys = policy.sortingKeys ?? EMPTY_KEYS;
  const filterKeys = policy.filterKeys ?? EMPTY_KEYS;
  const sortingSignature = sortingKeys.join('\u0000');
  const filterSignature = filterKeys.join('\u0000');

  const [state, setState] = useState<T>(() => {
    const storages = getTableStorages();
    if (!userId || !storages) return defaults;
    return readStoredTableStateWithPolicy(storages, userId, tableId, defaults, {
      sortingKeys,
      filterKeys,
      persistSorting,
      persistFilters,
    });
  });

  useEffect(() => {
    const storages = getTableStorages();
    if (!userId || !storages) {
      setState(defaults);
      return;
    }
    setState(readStoredTableStateWithPolicy(storages, userId, tableId, defaults, {
      sortingKeys,
      filterKeys,
      persistSorting,
      persistFilters,
    }));
  }, [
    defaults,
    filterKeys,
    filterSignature,
    persistFilters,
    persistSorting,
    sortingKeys,
    sortingSignature,
    tableId,
    userId,
  ]);

  useEffect(() => {
    const storages = getTableStorages();
    if (!userId || !storages) return;
    writeStoredTableStateWithPolicy(storages, userId, tableId, state, {
      sortingKeys,
      filterKeys,
      persistSorting,
      persistFilters,
    });
  }, [
    filterKeys,
    filterSignature,
    persistFilters,
    persistSorting,
    sortingKeys,
    sortingSignature,
    state,
    tableId,
    userId,
  ]);

  return [state, setState] as const;
}
